import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDraftStoreList,
  getScrapeJobForStore,
  type DraftStoreRef,
} from "@/api/adminService";
import { fetchStore } from "@/api/storeService";

type Args = { currentId: string | undefined; enabled?: boolean };

export function useDraftReviewQueue({ currentId, enabled = true }: Args) {
  const queryClient = useQueryClient();

  const { data: response } = useQuery({
    queryKey: ["draft-store-list"],
    queryFn: getDraftStoreList,
    staleTime: 30_000,
    enabled,
  });

  const drafts: DraftStoreRef[] = useMemo(
    () => (response?.ok ? response.data || [] : []),
    [response],
  );

  // Match by id OR slug (page may be loaded by either)
  const currentIndex = useMemo(() => {
    if (!currentId) return -1;
    return drafts.findIndex((d) => d._id === currentId || d.slug === currentId);
  }, [drafts, currentId]);

  const prev = currentIndex > 0 ? drafts[currentIndex - 1] : null;
  const next =
    currentIndex >= 0 && currentIndex < drafts.length - 1
      ? drafts[currentIndex + 1]
      : null;

  // Prefetch adjacent stores + scrape jobs so prev/next is instant
  useEffect(() => {
    if (!enabled) return;
    const prefetch = (ref: DraftStoreRef | null) => {
      if (!ref) return;
      void queryClient.prefetchQuery({
        queryKey: ["store", ref.slug || ref._id],
        queryFn: async () => {
          const response = await fetchStore({ id: ref.slug || ref._id });
          return response.data;
        },
        staleTime: 1000 * 60 * 2,
      });
      void queryClient.prefetchQuery({
        queryKey: ["store-scrape-job", ref._id],
        queryFn: () => getScrapeJobForStore({ storeId: ref._id }),
        staleTime: 1000 * 60 * 5,
      });
    };
    prefetch(prev);
    prefetch(next);
  }, [prev, next, enabled, queryClient]);

  return {
    drafts,
    currentIndex,
    total: drafts.length,
    prev,
    next,
    refetch: () =>
      queryClient.invalidateQueries({ queryKey: ["draft-store-list"] }),
  };
}
